import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { fmtDateTimeParis } from "../lib/date";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import UpsellStrip from "../components/UpsellStrip";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar, MapPin, Users, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [seats, setSeats] = useState(1);
  const [addons, setAddons] = useState([]);
  const [addonQty, setAddonQty] = useState({}); // sku -> qty
  const [submitting, setSubmitting] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftValidating, setGiftValidating] = useState(false);
  const [giftCard, setGiftCard] = useState(null);
  const [giftError, setGiftError] = useState("");
  // Personalisation (ateliers création + cadeau + préférences)
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [isGift, setIsGift] = useState(false);
  const [giftOfferedBy, setGiftOfferedBy] = useState("");
  const [giftMessage, setGiftMessage] = useState("");
  const [giftRecipientEmail, setGiftRecipientEmail] = useState("");
  const [fruitPref, setFruitPref] = useState("mix");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/events/${id}`).then(({ data }) => setEvent(data)).catch(() => setEvent(false));
    api.get("/snack-addons").then(({ data }) => setAddons(data)).catch(() => {});
  }, [id]);

  const isAfterwork = event && event.type === "afterwork";
  const isAtelier = event && (event.type || "").startsWith("atelier");
  const isRhumWorkshop = event && event.type === "atelier_rhum";

  // Prefill contact info from user profile once loaded
  useEffect(() => {
    if (user) {
      setContactName((prev) => prev || user.name || "");
      setContactEmail((prev) => prev || user.email || "");
      setContactPhone((prev) => prev || user.phone || "");
    }
  }, [user]);

  const snacksTotal = useMemo(() => {
    if (!isAfterwork) return 0;
    return addons.reduce((sum, a) => sum + (addonQty[a.sku] || 0) * a.price, 0);
  }, [addons, addonQty, isAfterwork]);

  const total = useMemo(() => {
    if (!event) return 0;
    return event.price * (parseInt(seats, 10) || 0) + snacksTotal;
  }, [event, seats, snacksTotal]);

  const giftDeduction = useMemo(() => {
    if (!giftCard) return 0;
    return Math.min(giftCard.current_balance, total);
  }, [giftCard, total]);

  const finalTotal = Math.max(total - giftDeduction, 0);

  if (event === null) return <div className="max-w-7xl mx-auto px-6 py-16 text-muted-foreground">Chargement…</div>;
  if (event === false) return <div className="max-w-7xl mx-auto px-6 py-16">Événement introuvable.</div>;

  const setQty = (sku, delta) => {
    setAddonQty((prev) => {
      const cur = prev[sku] || 0;
      const next = Math.max(0, cur + delta);
      return { ...prev, [sku]: next };
    });
  };

  const validateGiftCode = async () => {
    const code = giftCode.trim().toUpperCase();
    if (!code) {
      setGiftCard(null);
      setGiftError("");
      return;
    }
    setGiftValidating(true);
    setGiftError("");
    try {
      const { data } = await api.post("/gift-cards/validate", { code });
      setGiftCard(data);
      toast.success(`Carte validée — solde ${data.current_balance.toFixed(2)} €`);
    } catch (err) {
      setGiftCard(null);
      setGiftError(formatApiError(err.response?.data?.detail) || "Code invalide");
    } finally {
      setGiftValidating(false);
    }
  };

  const removeGiftCode = () => {
    setGiftCode("");
    setGiftCard(null);
    setGiftError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Veuillez vous connecter pour réserver.");
      nav("/connexion?next=" + encodeURIComponent(`/evenements/${id}`));
      return;
    }
    setSubmitting(true);
    try {
      // Atelier required-fields client guard
      if (isAtelier) {
        if (!contactName.trim()) {
          toast.error("Veuillez renseigner votre nom et prénom.");
          setSubmitting(false);
          return;
        }
        if (!contactPhone.trim()) {
          toast.error("Veuillez renseigner votre numéro de téléphone.");
          setSubmitting(false);
          return;
        }
        const emailClean = contactEmail.trim();
        if (!emailClean) {
          toast.error("Veuillez renseigner votre adresse email.");
          setSubmitting(false);
          return;
        }
        // basic email shape check
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
          toast.error("Adresse email invalide.");
          setSubmitting(false);
          return;
        }
      }
      const snack_add_ons = Object.entries(addonQty)
        .filter(([, qty]) => qty > 0)
        .map(([sku, quantity]) => ({ sku, quantity }));
      const { data } = await api.post("/event-bookings/checkout", {
        event_id: id,
        num_seats: parseInt(seats, 10),
        snack_add_ons,
        origin_url: window.location.origin,
        gift_card_code: giftCard?.code || null,
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim().toLowerCase() || null,
        contact_phone: contactPhone.trim() || null,
        is_gift: isGift,
        gift_offered_by: isGift ? (giftOfferedBy.trim() || null) : null,
        gift_message: isGift ? (giftMessage.trim() || null) : null,
        gift_recipient_email: isGift ? (giftRecipientEmail.trim().toLowerCase() || null) : null,
        fruit_preference: isRhumWorkshop ? fruitPref : null,
      });
      if (data.fully_covered) {
        toast.success("Réservation confirmée — payée par carte cadeau !");
        nav("/compte");
        return;
      }
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20" data-testid="event-detail-page">
      <div className="grid lg:grid-cols-2 gap-12">
        <div className="rounded-xl overflow-hidden bg-card border border-border">
          {event.image && <img src={event.image} alt={event.title} className="w-full h-[420px] object-cover" />}
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{event.type.replace("_", " ")}</div>
          <h1 className="font-display text-4xl lg:text-5xl mb-5" data-testid="event-title">{event.title}</h1>
          <p className="text-muted-foreground leading-relaxed mb-8">{event.description}</p>

          <ul className="space-y-3 mb-8 text-sm">
            <li className="flex items-center gap-3"><Calendar className="w-4 h-4 text-primary" />{fmtDateTimeParis(event.date)}</li>
            {event.location && <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-primary" />{event.location}</li>}
            <li className="flex items-center gap-3">
              <Users className="w-4 h-4 text-primary" />
              {event.seats_available <= 0 ? (
                <span className="text-rose-400 font-semibold uppercase tracking-[0.15em]">Complet — plus de places disponibles</span>
              ) : event.seats_available <= 3 ? (
                <span className="text-orange-400 font-semibold animate-pulse" data-testid="event-low-seats-banner">
                  ⚡ Plus que {event.seats_available} place{event.seats_available > 1 ? "s" : ""} sur {event.seats_total} — réservez vite !
                </span>
              ) : (
                <span>{event.seats_available} place(s) disponible(s) sur {event.seats_total}</span>
              )}
            </li>
          </ul>

          <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 lg:p-8 space-y-5" data-testid="booking-form">
            <div>
              <Label htmlFor="seats" className="mb-2 inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground">Nombre de places</Label>
              <Input
                id="seats"
                type="number"
                min={1}
                max={event.seats_available}
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                data-testid="seats-input"
                className="bg-input"
              />
            </div>

            {isAtelier && (
              <div className="rounded-lg border border-primary/20 bg-input/40 p-4 space-y-4" data-testid="atelier-contact-section">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-primary mb-1">Vos coordonnées</div>
                  <p className="text-xs text-muted-foreground mb-4">Nécessaires pour vous envoyer le reçu de paiement et vous joindre en cas d&apos;imprévu.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact-name" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Nom & prénom *</Label>
                    <Input
                      id="contact-name"
                      type="text"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      placeholder="Marie Dupont"
                      data-testid="contact-name-input"
                      className="bg-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-phone" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Téléphone *</Label>
                    <Input
                      id="contact-phone"
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="06 12 34 56 78"
                      data-testid="contact-phone-input"
                      className="bg-input"
                      required
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="contact-email" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Adresse email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      placeholder="marie.dupont@email.com"
                      data-testid="contact-email-input"
                      className="bg-input"
                      required
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">Votre reçu de paiement sera envoyé à cette adresse.</p>
                  </div>
                </div>

                {/* Gift toggle */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="gift-toggle-label">
                    <input
                      type="checkbox"
                      checked={isGift}
                      onChange={(e) => setIsGift(e.target.checked)}
                      className="w-4 h-4 accent-primary"
                      data-testid="gift-toggle-input"
                    />
                    <span className="text-sm">🎁 Cet atelier est un cadeau pour quelqu&apos;un d&apos;autre</span>
                  </label>
                </div>

                {isGift && (
                  <div className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4" data-testid="gift-personalisation-block">
                    <div>
                      <Label htmlFor="gift-from" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Offert par (votre nom)</Label>
                      <Input
                        id="gift-from"
                        type="text"
                        value={giftOfferedBy}
                        onChange={(e) => setGiftOfferedBy(e.target.value)}
                        placeholder="Optionnel — apparaîtra sur le reçu"
                        data-testid="gift-from-input"
                        className="bg-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gift-msg" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Message personnalisé</Label>
                      <textarea
                        id="gift-msg"
                        rows={3}
                        maxLength={500}
                        value={giftMessage}
                        onChange={(e) => setGiftMessage(e.target.value)}
                        placeholder="Joyeux anniversaire ! Profitez bien de cet atelier…"
                        data-testid="gift-message-input"
                        className="w-full rounded-md bg-input border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                      <div className="text-[10px] text-muted-foreground text-right mt-1">{giftMessage.length}/500</div>
                    </div>
                    <div>
                      <Label htmlFor="gift-recipient-email" className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Email du destinataire <span className="text-muted-foreground/60 normal-case tracking-normal">(optionnel)</span></Label>
                      <Input
                        id="gift-recipient-email"
                        type="email"
                        value={giftRecipientEmail}
                        onChange={(e) => setGiftRecipientEmail(e.target.value)}
                        placeholder="bon-cadeau@destinataire.com"
                        data-testid="gift-recipient-email-input"
                        className="bg-input"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        S&apos;il est rempli, l&apos;e-cadeau PDF sera envoyé <strong>directement</strong> à cette adresse.
                        Sinon il vous sera envoyé avec le reçu pour que vous le remettiez vous-même.
                      </p>
                    </div>
                  </div>
                )}

                {/* Fruit preference — Rhum Arrangé only */}
                {isRhumWorkshop && (
                  <div data-testid="fruit-preference-block">
                    <Label className="mb-2 inline-block text-xs uppercase tracking-[0.18em] text-muted-foreground">Préférence de fruits (optionnel)</Label>
                    <p className="text-[11px] text-muted-foreground mb-3">Pour préparer votre rhum arrangé selon vos goûts.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { id: "rouges", label: "🍓 Rouges" },
                        { id: "coques", label: "🌰 À coques" },
                        { id: "tropicaux", label: "🥭 Tropicaux" },
                        { id: "mix", label: "✨ Mix de tout" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setFruitPref(opt.id)}
                          className={`text-xs px-3 py-2 rounded-lg border transition-colors text-center ${
                            fruitPref === opt.id
                              ? "border-primary bg-primary/20 text-primary"
                              : "border-border bg-input/40 text-muted-foreground hover:border-primary/50"
                          }`}
                          data-testid={`fruit-pref-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {isAfterwork && addons.length > 0 && (
              <div className="rounded-lg border border-primary/20 bg-input/40 p-4" data-testid="snack-addons-section">
                <div className="text-xs uppercase tracking-[0.25em] text-primary mb-1">Ajouter du snacking</div>
                <p className="text-xs text-muted-foreground mb-4">Proposez une restauration à vos invités — tarif par personne.</p>
                <div className="space-y-4">
                  {addons.map((a) => (
                    <div key={a.sku} className="flex items-start gap-3" data-testid={`addon-${a.sku}`}>
                      {a.image && (
                        <img src={a.image} alt={a.name} className="w-20 h-20 object-cover rounded-lg border border-border shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-sm">{a.name}</div>
                          <div className="gold-text text-sm whitespace-nowrap">{a.price.toFixed(2)} € / pers.</div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{a.description}</p>
                        <div className="mt-3 flex items-center gap-3">
                          <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(a.sku, -1)} data-testid={`addon-${a.sku}-minus`}>
                            <Minus className="w-3 h-3" />
                          </Button>
                          <span className="text-sm w-6 text-center" data-testid={`addon-${a.sku}-qty`}>{addonQty[a.sku] || 0}</span>
                          <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setQty(a.sku, 1)} data-testid={`addon-${a.sku}-plus`}>
                            <Plus className="w-3 h-3" />
                          </Button>
                          {(addonQty[a.sku] || 0) > 0 && (
                            <span className="text-xs text-muted-foreground ml-auto">
                              = {((addonQty[a.sku] || 0) * a.price).toFixed(2)} €
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-border pt-5 space-y-1.5" data-testid="booking-summary">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Places ({seats} × {event.price.toFixed(2)} €)</span>
                <span>{(event.price * (parseInt(seats, 10) || 0)).toFixed(2)} €</span>
              </div>
              {snacksTotal > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Snacking</span>
                  <span>{snacksTotal.toFixed(2)} €</span>
                </div>
              )}
              {giftDeduction > 0 && (
                <div className="flex justify-between text-sm text-primary" data-testid="gift-deduction-line">
                  <span>Carte cadeau ({giftCard.code})</span>
                  <span>− {giftDeduction.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between items-end pt-2">
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Total</span>
                <span className="font-display text-3xl gold-text" data-testid="booking-total">{finalTotal.toFixed(2)} €</span>
              </div>
            </div>

            {/* Upsell — visible dès que l'utilisateur configure sa réservation */}
            {parseInt(seats, 10) > 0 && (
              <UpsellStrip context="event_booking" cartTotal={finalTotal} limit={2} compact />
            )}

            {/* Gift code */}
            <div className="rounded-lg border border-border bg-input/40 p-4" data-testid="gift-code-section">
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Carte cadeau</div>
              {!giftCard ? (
                <div className="flex gap-2">
                  <Input
                    value={giftCode}
                    onChange={(e) => setGiftCode(e.target.value)}
                    placeholder="SN-XXXX-XXXX"
                    className="bg-input font-mono"
                    data-testid="gift-code-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); validateGiftCode(); } }}
                  />
                  <Button type="button" variant="outline" onClick={validateGiftCode} disabled={giftValidating || !giftCode.trim()} data-testid="gift-code-apply-btn">
                    {giftValidating ? "…" : "Appliquer"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3" data-testid="gift-code-applied">
                  <div className="text-sm">
                    <span className="font-mono gold-text">{giftCard.code}</span>{" "}
                    <span className="text-muted-foreground">· solde {giftCard.current_balance.toFixed(2)} €</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={removeGiftCode} data-testid="gift-code-remove">
                    Retirer
                  </Button>
                </div>
              )}
              {giftError && <div className="text-xs text-destructive mt-2" data-testid="gift-code-error">{giftError}</div>}
            </div>

            <Button
              type="submit"
              disabled={submitting || event.seats_available < 1}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12"
              data-testid="confirm-booking-btn"
            >
              {event.seats_available < 1
                ? "Complet"
                : submitting
                  ? "Redirection…"
                  : finalTotal <= 0
                    ? "Confirmer la réservation"
                    : `Payer ${finalTotal.toFixed(2)} €`}
            </Button>
            {!user && <p className="text-xs text-muted-foreground text-center">Connexion requise pour finaliser la réservation.</p>}
          </form>
        </div>
      </div>
    </div>
  );
}
